import moment, { type Moment, type MomentInput } from "moment/moment";
import {
  BALANCE_DETAIL_AMOUNT_KEYS,
  BalanceDomain,
  BalanceType,
  BalanceTypeCategory,
  DOMAIN_TO_PARENT_ID_FIELD_NAME,
  DOMAINE_ID_SEPARATOR,
  RENT_CALL_TYPES,
  TAX_REGIME_BY_STATUS_FISCAL,
} from "./constants/balances";
import { PropertyType } from "../models/property";
import type { Property } from "../models/property";
import type { PropertyFolder } from "../models/propertyFolder";
import type { Tenant } from "../models/tenant";
import type {
  Balance,
  BalanceDetails,
  BalanceTypeCatalog,
  BalanceTypeDefinition,
  BalanceTypeFamily,
} from "../models/balance";
import type { ProfileBailleur } from "../components/Profile/types";
import {
  buildPropertyFoldersTree,
  findPropertyFolder,
  getIncludedPropertyAndFolderIds,
  // @ts-expect-error pure js
} from "./properties";
// @ts-expect-error pure js
import { TenantStatus } from "../models/tenant_old.js";

export interface BalanceTypeOption {
  label: string;
  value: string;
  family?: string;
}

export interface BalanceTypeOptionGroup {
  label: string;
  options: BalanceTypeOption[];
}

interface BalanceTypeOptionsFilters {
  isEdit?: boolean;
  excludeCategories?: string[];
  excludeFamilies?: string[];
  taxRegimes?: Set<string> | null;
  /** Compte mandataire : propose aussi les opérations réservées aux agences. */
  isAgency?: boolean;
  family?: string | null;
}

type ParentIdFieldName =
  (typeof DOMAIN_TO_PARENT_ID_FIELD_NAME)[keyof typeof DOMAIN_TO_PARENT_ID_FIELD_NAME];

type BalanceAffectationFields = Pick<Balance, "domaine" | ParentIdFieldName>;

type PeriodTenant = Pick<
  Tenant,
  "debut" | "payment_recurrence" | "payment_periods_from_start_date"
>;

export interface AffectationSubtree {
  profileIds: Set<number>;
  folderIds: Set<number>;
  propertyIds: Set<number>;
  tenantIds: Set<number>;
}

export interface AffectationContext {
  domaine?: string | null;
  parentId?: number | string | null;
}

export interface Affectation {
  domaine: string;
  parentId: number;
}

export interface ResolvedAffectation extends Affectation {
  kept: boolean;
}

interface AffectationCollections {
  properties?: Property[];
  propertyFolders?: PropertyFolder[];
  tenants?: Tenant[];
  profileBailleurs?: ProfileBailleur[];
}

// Un candidat n'a pas de bail : aucune écriture ne lui est affectée.
const AFFECTABLE_TENANT_STATUSES: number[] = [
  TenantStatus.Actif.status,
  TenantStatus.Archive.status,
];

export function isAffectableTenant(tenant: Pick<Tenant, "status">) {
  return AFFECTABLE_TENANT_STATUSES.includes(tenant.status);
}

/** Sans `withFolders`, seuls les biens comptables sont affectables parmi les dossiers. */
export function isAffectableFolder(
  folder: Pick<PropertyFolder, "property_type">,
  withFolders = false,
) {
  return withFolders || folder.property_type === PropertyType.BIEN_COMPTABLE;
}

/**
 * Liste des types de balance autorisés pour une affectation (domaine) donnée,
 * sous forme d'options `{ label, value, family }` triées. La règle type/domaine vient du
 * catalogue `/balance-types` (champ `domains`), qui la sert aussi au back.
 * `taxRegimes` (Set) ne garde que les types proposés à l'un de ces régimes ; `family` ne
 * garde que les types de cette catégorie d'opération, `excludeCategories` et
 * `excludeFamilies` écartent ceux d'une catégorie technique ou d'une famille. Les
 * opérations réservées aux agences ne sont proposées qu'avec `isAgency`.
 * Renvoie `[]` si le domaine est inconnu/absent.
 */
export function getBalanceTypeOptions(
  domaine: string | null | undefined,
  { balanceTypes }: { balanceTypes: BalanceTypeCatalog | undefined },
  {
    isEdit = false,
    excludeCategories = [],
    excludeFamilies = [],
    taxRegimes = null,
    isAgency = false,
    family = null,
  }: BalanceTypeOptionsFilters = {},
): BalanceTypeOption[] {
  if (!balanceTypes || !domaine) return [];

  return Object.values(balanceTypes)
    .filter((balanceType) => balanceType.domains.includes(domaine))
    .filter((balanceType) => !excludeCategories.includes(balanceType.category))
    .filter((balanceType) => !excludeFamilies.includes(balanceType.family))
    .filter((balanceType) =>
      isEdit ? balanceType.edition : balanceType.creation,
    )
    .filter((balanceType) => isAgency || !balanceType.agency_only)
    .filter((balanceType) => !family || balanceType.family === family)
    .filter(
      (balanceType) =>
        !taxRegimes ||
        balanceType.tax_regimes.some((regime) => taxRegimes.has(regime)),
    )
    .map((balanceType) => ({
      label: balanceType.text,
      value: balanceType.static_id,
      family: balanceType.family,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

/**
 * Catégories d'opération représentées dans des options de types, dans l'ordre du
 * catalogue des familles.
 */
export function getBalanceTypeFamilyOptions(
  typeOptions: BalanceTypeOption[],
  families: BalanceTypeFamily[] = [],
): BalanceTypeOption[] {
  return families
    .filter((family) =>
      typeOptions.some((option) => option.family === family.key),
    )
    .map((family) => ({ label: family.label, value: family.key }));
}

/**
 * Options de types regroupées par catégorie d'opération, dans l'ordre du catalogue des
 * familles — pour un select unique cherchable.
 */
export function groupBalanceTypeOptionsByFamily(
  typeOptions: BalanceTypeOption[],
  families: BalanceTypeFamily[] = [],
): BalanceTypeOptionGroup[] {
  return families
    .map((family) => ({
      label: family.label,
      options: typeOptions.filter((option) => option.family === family.key),
    }))
    .filter((group) => group.options.length > 0);
}

/**
 * Ce que le type fait fiscalement, en une ligne, pour les types côté bien : déductible ou
 * non des revenus fonciers (avec la ligne de la 2044, ou de la 2072-S pour une SCI à
 * l'IR), récupérable sur le locataire ou non. Rien côté locataire ni pour un régime qui ne
 * déclare pas de revenus fonciers.
 */
export function getBalanceTypeFiscalHint(
  balanceType: BalanceTypeDefinition | null | undefined,
  taxRegimes: Set<string> | null = null,
  form2072 = false,
) {
  if (!balanceType || !balanceType.domains?.includes(BalanceDomain.PROPERTY)) {
    return null;
  }
  const parts = [];
  if (!taxRegimes || taxRegimes.has("revenu_foncier")) {
    const line = form2072
      ? balanceType.category_impot_2072
      : balanceType.category_impot;
    const form = form2072 ? "2072-S" : "2044";
    if (balanceType.is_deductible) {
      parts.push(
        line ? `Déductible (ligne ${line} de la ${form})` : "Déductible",
      );
    } else if (balanceType.category === BalanceTypeCategory.CREDIT) {
      parts.push(line ? `Recette (ligne ${line} de la ${form})` : "Recette");
    } else {
      parts.push("Non déductible des revenus fonciers");
    }
  }
  const partial = (balanceType.details ?? []).some(
    (detail) => detail.is_recuperable,
  );
  if (partial) {
    parts.push("part récupérable sur le locataire selon le détail");
  } else if (balanceType.is_recuperable) {
    parts.push("récupérable sur le locataire");
  }
  return parts.length > 0 ? parts.join(" · ") : null;
}

/**
 * Détail du montant tel que le back l'attend : les sous-montants saisis convertis en
 * nombres, l'énergie si choisie, `null` quand rien n'est renseigné.
 */
export function toBalanceDetailsPayload(
  details: Record<string, unknown> | null | undefined,
): BalanceDetails | null {
  if (!details) return null;
  const payload: BalanceDetails = {};
  for (const key of BALANCE_DETAIL_AMOUNT_KEYS) {
    const value = details[key];
    if (value === "" || value === null || value === undefined) continue;
    const amount = Number(value);
    if (!Number.isNaN(amount)) payload[key] = amount;
  }
  if (details.energy) payload.energy = details.energy as string;
  return Object.keys(payload).length > 0 ? payload : null;
}

export function formatBalanceAffectation(
  domaine: string | undefined,
  parentId: number | string | undefined,
) {
  return `${domaine}${DOMAINE_ID_SEPARATOR}${parentId}`;
}

export function getParentIdPropertyFromDomain(
  domaineBalance: string | undefined,
): ParentIdFieldName | undefined {
  const fieldNames: Partial<Record<string, ParentIdFieldName>> =
    DOMAIN_TO_PARENT_ID_FIELD_NAME;
  return domaineBalance ? fieldNames[domaineBalance] : undefined;
}

export function getBalanceParentId(balance: BalanceAffectationFields) {
  const fieldName = getParentIdPropertyFromDomain(balance.domaine);
  return fieldName ? balance[fieldName] : undefined;
}

export function getBalanceAffectation(balance: BalanceAffectationFields) {
  return formatBalanceAffectation(balance.domaine, getBalanceParentId(balance));
}

export function shouldSplitBalance(
  domaine: string | undefined,
  parentId: number | string | undefined,
  propertyFoldersById: Record<string, PropertyFolder>,
) {
  return (
    domaine === BalanceDomain.FOLDER &&
    parentId !== undefined &&
    propertyFoldersById[parentId]?.property_type ===
      PropertyType.BIEN_NON_COMPTABLE
  );
}

/**
 * Une ligne a-t-elle quelque chose à refacturer : un type récupérable sans détail l'est en
 * entier ; un type dont seuls des sous-montants sont récupérables (TEOM d'une taxe
 * foncière, part locataire d'un appel de fonds) ne l'est que si l'un d'eux est saisi.
 */
export function isChildRecuperable(
  child:
    | { type?: string; details?: Record<string, unknown> | null }
    | null
    | undefined,
  balanceTypes: BalanceTypeCatalog | undefined,
) {
  if (!child?.type || !balanceTypes) {
    return false;
  }

  const balanceType = balanceTypes[child.type];
  const recuperableKeys = (balanceType?.details ?? [])
    .filter((detail) => detail.is_recuperable)
    .map((detail) => detail.key);
  if (recuperableKeys.length === 0) return balanceType?.is_recuperable === true;
  return recuperableKeys.some((key) => Number(child.details?.[key]) > 0);
}

/**
 * Part d'une charge récupérable imputable au locataire : prorata temporis de sa présence sur la
 * période de facturation de la charge (mêmes règles que `montant_recuperable_prorata` côté back).
 */
export function calculateProrata(
  balance: {
    montant: number;
    periode_debut?: string | null;
    periode_fin?: string | null;
  },
  tenant: Pick<Tenant, "debut" | "fin">,
) {
  if (!balance.periode_debut || !balance.periode_fin) return balance.montant;

  const periodeDebut = moment(balance.periode_debut);
  const periodeFin = moment(balance.periode_fin);
  const tenantDebut = moment(tenant.debut);
  const tenantFin = tenant.fin ? moment(tenant.fin) : moment();

  const intersectionDebut = moment.max(periodeDebut, tenantDebut);
  const intersectionFin = moment.min(periodeFin, tenantFin);

  if (intersectionDebut.isAfter(intersectionFin)) return 0;

  const periodeDays = periodeFin.diff(periodeDebut, "days") + 1;
  const intersectionDays = intersectionFin.diff(intersectionDebut, "days") + 1;

  if (intersectionDays === periodeDays) return balance.montant;

  return (
    Math.round((balance.montant / periodeDays) * intersectionDays * 100) / 100
  );
}

/** Charge récupérable enregistrée mais encore refacturée à aucun locataire. */
export function isChargeARegulariser(
  balance: Pick<Balance, "type" | "refacturations">,
  balanceTypes: BalanceTypeCatalog | undefined,
) {
  return (
    isChildRecuperable(balance, balanceTypes) &&
    Object.keys(balance.refacturations ?? {}).length === 0
  );
}

export function initializeRefacturationValues(
  balance: Pick<Balance, "refacturations"> | null | undefined,
) {
  return Object.entries(balance?.refacturations ?? {}).map(
    ([tenantId, date]) => ({
      tenant_id: tenantId,
      date: date,
    }),
  );
}

/**
 * Mois (1-12) sur lequel les périodes d'appel du bail sont alignées : celui du début
 * de bail si le bail le demande, sinon janvier (périodes civiles). Sans effet en mensuel.
 */
export function getPeriodAnchorMonth(tenant: PeriodTenant | null | undefined) {
  if (
    tenant?.payment_periods_from_start_date &&
    tenant?.debut &&
    Number(tenant?.payment_recurrence ?? 1) > 1
  ) {
    return moment(tenant.debut).month() + 1;
  }
  return 1;
}

/** Premier jour (moment) de la période d'appel contenant `date`. */
export function getPeriodStart(
  date: MomentInput,
  paymentRecurrence: number | string | null | undefined,
  periodAnchorMonth = 1,
): Moment {
  const m = moment(date).startOf("month");
  const recurrence = Number(paymentRecurrence ?? 1);
  if (recurrence <= 1) return m;
  const offset =
    (((m.month() + 1 - periodAnchorMonth) % recurrence) + recurrence) %
    recurrence;
  return m.subtract(offset, "months");
}

/** Mois "MM/YYYY" qui ouvre la période d'appel du bail contenant `month` ("MM/YYYY"). */
export function getTenantPeriodStartMonth(
  tenant: PeriodTenant | null | undefined,
  month: string,
) {
  return getPeriodStart(
    moment(month, "MM/YYYY"),
    tenant?.payment_recurrence,
    getPeriodAnchorMonth(tenant),
  ).format("MM/YYYY");
}

/** Réduit une liste de mois "MM/YYYY" (triée) à un mois par période d'appel du bail. */
export function getTenantPeriodMonths(
  tenant: PeriodTenant | null | undefined,
  months: string[],
) {
  const seen = new Set<string>();
  const result: string[] = [];
  months.forEach((month) => {
    const periodMonth = getTenantPeriodStartMonth(tenant, month);
    if (!seen.has(periodMonth)) {
      seen.add(periodMonth);
      result.push(periodMonth);
    }
  });
  return result;
}

/** Libellé de la période d'appel du bail contenant `month` : "août 2026", "T3 2026" ou une plage de dates. */
export function getTenantPeriodLabel(
  tenant: PeriodTenant | null | undefined,
  month: string,
) {
  const recurrence = Number(tenant?.payment_recurrence ?? 1);
  const m = moment(month, "MM/YYYY");
  if (recurrence <= 1) return m.format("MMMM YYYY");
  return formatBalancePeriodLabel(m, recurrence, getPeriodAnchorMonth(tenant));
}

export function formatBalancePeriodLabel(
  date: MomentInput,
  paymentRecurrence = 1,
  periodAnchorMonth = 1,
) {
  const m = moment(date);
  if (paymentRecurrence > 1 && periodAnchorMonth !== 1) {
    const start = getPeriodStart(m, paymentRecurrence, periodAnchorMonth);
    const end = start
      .clone()
      .add(paymentRecurrence - 1, "months")
      .endOf("month");
    return `${start.format("DD/MM/YYYY")} - ${end.format("DD/MM/YYYY")}`;
  }
  if (paymentRecurrence === 3) {
    const quarter = Math.floor(m.month() / 3) + 1;
    return `T${quarter} ${m.year()}`;
  }
  if (paymentRecurrence === 6) {
    const semester = m.month() < 6 ? 1 : 2;
    return `S${semester} ${m.year()}`;
  }
  if (paymentRecurrence === 12) {
    return `${m.year()}`;
  }
  return m.format("MMMM");
}

/**
 * Entités auxquelles une écriture peut être affectée depuis un contexte donné : l'entité
 * elle-même et tout ce qui pend en dessous, jamais un ascendant. Un contexte absent ouvre
 * le portefeuille entier, ce dont se servent les écrans sans page de rattachement
 * (rapprochement bancaire, ventilation, configuration financière).
 *
 * Le sous-arbre retient exactement les entités que propose `AffectationSelect` : les
 * catégories, le compte des cibles et la cible posée d'office en dérivent, et une
 * entité que le sélecteur ne liste pas les fausserait.
 */
export function getAffectationSubtree(
  context: AffectationContext | null | undefined,
  collections: AffectationCollections = {},
  { withFolders = false }: { withFolders?: boolean } = {},
): AffectationSubtree {
  const {
    properties = [],
    propertyFolders = [],
    tenants = [],
    profileBailleurs = [],
  } = collections;

  const affectableFolderIds = (keep: (folder: PropertyFolder) => boolean) =>
    new Set(
      propertyFolders
        .filter(
          (folder) => keep(folder) && isAffectableFolder(folder, withFolders),
        )
        .map((folder) => folder.id),
    );

  const empty = (): AffectationSubtree => ({
    profileIds: new Set(),
    folderIds: new Set(),
    propertyIds: new Set(),
    tenantIds: new Set(),
  });

  if (!context?.domaine || !context?.parentId) {
    return {
      profileIds: new Set(profileBailleurs.map((profile) => profile.id)),
      folderIds: affectableFolderIds(() => true),
      propertyIds: new Set(properties.map((property) => property.id)),
      tenantIds: new Set(
        tenants.filter(isAffectableTenant).map((tenant) => tenant.id),
      ),
    };
  }

  const parentId = Number(context.parentId);
  // Les locataires ne sont jamais nommés directement par l'arborescence : ils suivent
  // les biens retenus.
  const withTenants = (subtree: AffectationSubtree): AffectationSubtree => ({
    ...subtree,
    tenantIds: new Set(
      tenants
        .filter(
          (tenant) =>
            subtree.propertyIds.has(tenant.id_bien) &&
            isAffectableTenant(tenant),
        )
        .map((tenant) => tenant.id),
    ),
  });

  switch (context.domaine) {
    case BalanceDomain.TENANT:
      return { ...empty(), tenantIds: new Set([parentId]) };
    case BalanceDomain.PROPERTY:
      return withTenants({ ...empty(), propertyIds: new Set([parentId]) });
    case BalanceDomain.FOLDER: {
      const tree = buildPropertyFoldersTree(propertyFolders, properties);
      const { propertyIds, folderIds } = getIncludedPropertyAndFolderIds(
        findPropertyFolder(tree.property_folders, parentId),
      );
      return withTenants({
        ...empty(),
        folderIds: affectableFolderIds((folder) => folderIds.has(folder.id)),
        propertyIds,
      });
    }
    case BalanceDomain.PROFILE_BAILLEUR:
      return withTenants({
        ...empty(),
        profileIds: new Set([parentId]),
        folderIds: affectableFolderIds(
          (folder) => folder.id_profile_nom_owner === parentId,
        ),
        propertyIds: new Set(
          properties
            .filter((property) => property.id_profile_nom_owner === parentId)
            .map((property) => property.id),
        ),
      });
    default:
      return empty();
  }
}

// Les domaines qui acceptent le même jeu de catégories partagent un groupe : `property` et
// `folder` proposent les mêmes charges, un groupe par domaine les afficherait deux fois.
// L'ordre va du haut de la hiérarchie de rattachement vers le bas.
const AFFECTATION_GROUPS: { label: string; domains: string[] }[] = [
  { label: "Bailleur", domains: [BalanceDomain.PROFILE_BAILLEUR] },
  {
    label: "Bien & dossier",
    domains: [BalanceDomain.FOLDER, BalanceDomain.PROPERTY],
  },
  { label: "Locataire", domains: [BalanceDomain.TENANT] },
];

const SUBTREE_KEY_BY_DOMAIN: Record<string, keyof AffectationSubtree> = {
  [BalanceDomain.PROFILE_BAILLEUR]: "profileIds",
  [BalanceDomain.FOLDER]: "folderIds",
  [BalanceDomain.PROPERTY]: "propertyIds",
  [BalanceDomain.TENANT]: "tenantIds",
};

export function getReachableDomains(subtree: AffectationSubtree) {
  return Object.entries(SUBTREE_KEY_BY_DOMAIN)
    .filter(([, key]) => subtree[key].size > 0)
    .map(([domaine]) => domaine);
}

/**
 * Sous-arbre ramené à un plafond de domaines : les entités des autres domaines en
 * disparaissent, et avec elles les catégories comme les affectations qui en dérivent.
 * Rend le sous-arbre tel quel quand aucun plafond n'est posé.
 */
export function restrictSubtreeToDomains(
  subtree: AffectationSubtree,
  domains: string[] | undefined,
): AffectationSubtree {
  if (!domains) return subtree;

  return Object.fromEntries(
    Object.entries(SUBTREE_KEY_BY_DOMAIN).map(([domaine, key]) => [
      key,
      domains.includes(domaine) ? subtree[key] : new Set<number>(),
    ]),
  ) as unknown as AffectationSubtree;
}

export function getAffectationDomains(
  type: string | null | undefined,
  balanceTypes: BalanceTypeCatalog | undefined,
) {
  return (type && balanceTypes?.[type]?.domains) || [];
}

/**
 * Profils bailleurs dont dépend un sous-arbre : les profils eux-mêmes, et les
 * propriétaires des dossiers, des biens et des biens des locataires atteignables.
 */
function getSubtreeProfiles(
  subtree: AffectationSubtree,
  collections: AffectationCollections = {},
): (ProfileBailleur | undefined)[] {
  const {
    properties = [],
    propertyFolders = [],
    tenants = [],
    profileBailleurs = [],
  } = collections;

  const propertyIds = new Set(subtree.propertyIds);
  for (const tenant of tenants) {
    if (subtree.tenantIds.has(tenant.id)) propertyIds.add(tenant.id_bien);
  }
  const profileIds = new Set(subtree.profileIds);
  for (const property of properties) {
    if (propertyIds.has(property.id))
      profileIds.add(property.id_profile_nom_owner);
  }
  for (const folder of propertyFolders) {
    if (subtree.folderIds.has(folder.id))
      profileIds.add(folder.id_profile_nom_owner);
  }
  return [...profileIds].map((profileId) =>
    profileBailleurs.find((p) => p.id === profileId),
  );
}

function profileTaxRegime(profile: ProfileBailleur | undefined) {
  return profile?.regime_fiscal
    ? TAX_REGIME_BY_STATUS_FISCAL[profile.regime_fiscal]
    : undefined;
}

/**
 * Régimes fiscaux des profils bailleurs dont dépend un sous-arbre.
 * Rend `null` — aucun filtre — dès qu'un de ces profils n'a pas de régime connu : mieux
 * vaut tout proposer que cacher le bon type.
 */
export function getSubtreeTaxRegimes(
  subtree: AffectationSubtree,
  collections: AffectationCollections = {},
): Set<string> | null {
  const regimes = new Set<string>();
  for (const profile of getSubtreeProfiles(subtree, collections)) {
    const regime = profileTaxRegime(profile);
    if (!regime) return null;
    regimes.add(regime);
  }
  return regimes.size > 0 ? regimes : null;
}

/**
 * Vrai quand tous les profils du sous-arbre déclarent une 2072-S (société aux revenus
 * fonciers, donc SCI à l'IR) : l'indication fiscale cite alors ses lignes plutôt que
 * celles de la 2044.
 */
export function subtreeUses2072(
  subtree: AffectationSubtree,
  collections: AffectationCollections = {},
): boolean {
  const profiles = getSubtreeProfiles(subtree, collections);
  return (
    profiles.length > 0 &&
    profiles.every(
      (profile) =>
        profile?.is_societe && profileTaxRegime(profile) === "revenu_foncier",
    )
  );
}

/**
 * Catégories d'opération proposables depuis un sous-arbre, groupées par famille de
 * rattachement comme `getBalanceTypeGroups`. Liste plate quand une seule famille est
 * atteignable.
 */
export function getBalanceTypeFamilyGroups(
  subtree: AffectationSubtree,
  {
    balanceTypes,
    families,
  }: {
    balanceTypes: BalanceTypeCatalog | undefined;
    families: BalanceTypeFamily[] | undefined;
  },
  options: BalanceTypeOptionsFilters = {},
): BalanceTypeOption[] | BalanceTypeOptionGroup[] {
  const reachable = getReachableDomains(subtree);
  const groups = AFFECTATION_GROUPS.map((group) => ({
    label: group.label,
    domaine: group.domains.find((domaine) => reachable.includes(domaine)),
  }))
    .filter((group) => group.domaine)
    .map((group) => ({
      label: group.label,
      options: getBalanceTypeFamilyOptions(
        getBalanceTypeOptions(group.domaine, { balanceTypes }, options),
        families,
      ),
    }))
    .filter((group) => group.options.length > 0);

  return groups.length <= 1 ? groups[0]?.options ?? [] : groups;
}

/**
 * Types proposables depuis un sous-arbre, tous domaines atteignables confondus (un type
 * ouvert au bien et au dossier n'apparaît qu'une fois).
 */
export function getBalanceTypeOptionsForSubtree(
  subtree: AffectationSubtree,
  { balanceTypes }: { balanceTypes: BalanceTypeCatalog | undefined },
  options: BalanceTypeOptionsFilters = {},
): BalanceTypeOption[] {
  const byValue = new Map<string, BalanceTypeOption>();
  for (const domaine of getReachableDomains(subtree)) {
    for (const option of getBalanceTypeOptions(
      domaine,
      { balanceTypes },
      options,
    )) {
      byValue.set(option.value, option);
    }
  }
  return [...byValue.values()].sort((a, b) => a.label.localeCompare(b.label));
}

/**
 * Catégories proposables depuis un sous-arbre, groupées par famille de rattachement.
 * Rend une liste plate quand une seule famille est atteignable — un en-tête unique
 * n'apprend rien.
 */
export function getBalanceTypeGroups(
  subtree: AffectationSubtree,
  { balanceTypes }: { balanceTypes: BalanceTypeCatalog | undefined },
  options: BalanceTypeOptionsFilters = {},
): BalanceTypeOption[] | BalanceTypeOptionGroup[] {
  const reachable = getReachableDomains(subtree);
  const groups = AFFECTATION_GROUPS.map((group) => ({
    label: group.label,
    domaine: group.domains.find((domaine) => reachable.includes(domaine)),
  }))
    .filter((group) => group.domaine)
    .map((group) => ({
      label: group.label,
      options: getBalanceTypeOptions(group.domaine, { balanceTypes }, options),
    }))
    .filter((group) => group.options.length > 0);

  return groups.length <= 1 ? groups[0]?.options ?? [] : groups;
}

/** Cibles du sous-arbre qu'autorisent les domaines de la catégorie. */
export function getAffectationCandidates(
  allowedDomains: string[],
  subtree: AffectationSubtree,
) {
  return allowedDomains.flatMap((domaine) =>
    [...subtree[SUBTREE_KEY_BY_DOMAIN[domaine]]].map((parentId) => ({
      domaine,
      parentId,
    })),
  );
}

/**
 * Ce que devient l'affectation quand la catégorie change : le candidat retenu, avec
 * `kept` disant s'il s'agit de l'affectation courante (encore valide) ou d'un candidat
 * unique posé d'office. Rend `null` quand aucun candidat ne s'impose.
 */
export function resolveAffectation(
  current: AffectationContext | null | undefined,
  allowedDomains: string[],
  subtree: AffectationSubtree,
): ResolvedAffectation | null {
  const candidates = getAffectationCandidates(allowedDomains, subtree);

  const kept = candidates.find(
    (candidate) =>
      candidate.domaine === current?.domaine &&
      candidate.parentId === Number(current?.parentId),
  );
  if (kept) return { ...kept, kept: true };

  return candidates.length === 1 ? { ...candidates[0], kept: false } : null;
}

interface BalanceTextOptions {
  balanceTypes?: BalanceTypeCatalog;
  domaineBalance?: string;
  propertiesById?: Record<number, Property>;
  tenantsById?: Record<number, Tenant>;
  paymentRecurrence?: number;
  periodAnchorMonth?: number;
}

/**
 * Appel locataire réglé à part du loyer : tout appel dû hors loyer, charges et dépôt
 * de garantie (charge exceptionnelle, taxe refacturée, réparation, honoraires…).
 * Son paiement solde l'appel seul, là où celui du loyer solde le mois entier.
 */
export function isStandaloneTenantCall(
  balance: Balance,
  balanceTypes?: BalanceTypeCatalog,
) {
  return (
    balanceTypes?.[balance.type]?.category ===
      BalanceTypeCategory.LOCATAIRE_APPEL &&
    !RENT_CALL_TYPES.includes(balance.type) &&
    balance.type !== BalanceType.APPEL_CHARGE &&
    balance.montant < 0
  );
}

export function getBalanceText(
  balance: Balance,
  options: BalanceTextOptions = {},
) {
  const {
    balanceTypes,
    domaineBalance,
    propertiesById = {},
    tenantsById = {},
    paymentRecurrence,
    periodAnchorMonth = 1,
  } = options;

  if (!balanceTypes?.[balance.type]) return "";

  // Un appel autre que le loyer ou les charges (appel complémentaire, taxe refacturée,
  // régularisation…) porte son objet en commentaire : c'est son vrai libellé.
  if (
    balanceTypes[balance.type].category ===
      BalanceTypeCategory.LOCATAIRE_APPEL &&
    !RENT_CALL_TYPES.includes(balance.type) &&
    balance.type !== BalanceType.APPEL_CHARGE &&
    balance.comment
  ) {
    return balance.comment;
  }

  let text = balanceTypes[balance.type].text;

  // Append period for rent/charge calls (month / T1-T4 / S1-S2 / year)
  if (
    RENT_CALL_TYPES.includes(balance.type) ||
    balance.type === BalanceType.APPEL_CHARGE
  ) {
    text = `${text} ${formatBalancePeriodLabel(
      balance.date_transac,
      paymentRecurrence,
      periodAnchorMonth,
    )}`;
  }

  // Vu depuis un dossier ou un profil bailleur, une opération d'un bien porte le nom du bien.
  if (
    (domaineBalance === BalanceDomain.FOLDER ||
      domaineBalance === BalanceDomain.PROFILE_BAILLEUR) &&
    balance.id_bien
  ) {
    const propertyName = propertiesById[balance.id_bien]?.nom;
    if (propertyName) {
      text = `${text} (${propertyName})`;
    }
  }

  if (
    (domaineBalance === BalanceDomain.PROPERTY ||
      domaineBalance === BalanceDomain.FOLDER ||
      domaineBalance === BalanceDomain.PROFILE_BAILLEUR) &&
    [
      BalanceType.PAIEMENT,
      BalanceType.PAIEMENT_DG,
      BalanceType.PAIEMENT_HONORAIRES,
    ].includes(balance.type) &&
    balance.id_tenant
  ) {
    const tenantName = tenantsById[balance.id_tenant]?.nom;
    if (tenantName) {
      text = `${text} ${tenantName}`;
    }
  }

  return text;
}

interface AffectationLabelOptions {
  tenantsById?: Record<number, Tenant>;
  propertiesById?: Record<number, Property>;
  propertyFoldersById?: Record<number, PropertyFolder>;
  profileBailleursById?: Record<number, ProfileBailleur>;
}

/**
 * Nom de l'entité visée par une affectation, pour l'afficher à l'utilisateur (ex. le toast
 * de création hors de la page). Le repli générique remplace un nom manquant, la table
 * correspondante n'étant pas toujours chargée au moment de l'appel.
 */
export function getAffectationLabel(
  domaine: string,
  parentId: number,
  options: AffectationLabelOptions = {},
) {
  const {
    tenantsById = {},
    propertiesById = {},
    propertyFoldersById = {},
    profileBailleursById = {},
  } = options;

  switch (domaine) {
    case BalanceDomain.TENANT:
      return tenantsById[parentId]?.nom ?? "ce locataire";
    case BalanceDomain.PROPERTY:
      return propertiesById[parentId]?.nom ?? "ce bien";
    case BalanceDomain.FOLDER:
      return propertyFoldersById[parentId]?.nom ?? "ce dossier";
    case BalanceDomain.PROFILE_BAILLEUR:
      return profileBailleursById[parentId]?.nom_profile ?? "ce profil";
    default:
      return "cette entité";
  }
}
